/**
 * Overlap Validation Utility
 *
 * Feature: 003-bug-fixes
 * Implements overlap detection for instructor reservations.
 */
/**
 * Check if two time ranges overlap.
 *
 * Two ranges [start1, end1) and [start2, end2) overlap if:
 *   start1 < end2 AND start2 < end1
 *
 * @example
 * // Adjacent (no overlap): 10:00-11:00 and 11:00-12:00
 * rangesOverlap(10, 1, 11, 1) // false
 *
 * // Overlap: 10:00-12:00 and 11:00-13:00
 * rangesOverlap(10, 2, 11, 2) // true
 */
export function rangesOverlap(start1, length1, start2, length2) {
    const end1 = start1 + length1;
    const end2 = start2 + length2;
    return start1 < end2 && start2 < end1;
}
/**
 * Find the first conflicting cell in a list of cells.
 *
 * @param cells - Existing reserved cells
 * @param hour - Proposed start hour
 * @param length - Proposed duration
 * @param excludeHour - Hour to exclude (for edit operations)
 * @returns Conflicting cell or null if no conflict
 */
export function findConflictingCell(cells, hour, length, excludeHour) {
    for (const cell of cells) {
        // Skip non-reserved cells (shouldn't happen with typed input)
        if (cell.type !== 'RESERVED')
            continue;
        // Skip the cell being edited
        if (excludeHour !== undefined && cell.hour === excludeHour)
            continue;
        // Check for overlap
        if (rangesOverlap(cell.hour, cell.length, hour, length)) {
            return cell;
        }
    }
    return null;
}
/**
 * Validate a proposed reservation time slot.
 *
 * Frontend validation function - called before save to provide immediate feedback.
 *
 * @param cells - Current cells for the instructor's row (filter to RESERVED only)
 * @param request - Proposed reservation details (without date, since we're validating within a loaded row)
 * @returns Validation result with conflict details if any
 */
export function validateOverlap(cells, request) {
    const { hour, length, instructorId, excludeCellId } = request;
    // Extract excludeHour from excludeCellId if provided
    // Format: `${date}_${instructorId}_${originalHour}`
    let excludeHour;
    if (excludeCellId) {
        const parts = excludeCellId.split('_');
        if (parts.length >= 3) {
            excludeHour = parseInt(parts[parts.length - 1], 10);
        }
    }
    const conflictingCell = findConflictingCell(cells, hour, length, excludeHour);
    if (conflictingCell) {
        const cellEnd = conflictingCell.hour + conflictingCell.length;
        return {
            valid: false,
            error: {
                code: 'OVERLAP_DETECTED',
                message: `Instruktor již má rezervaci od ${conflictingCell.hour}:00 do ${cellEnd}:00`,
                conflict: {
                    instructorId,
                    instructorName: '', // Will be filled by caller
                    hour: conflictingCell.hour,
                    length: conflictingCell.length,
                    service: conflictingCell.service,
                    clientNames: conflictingCell.clientNames,
                },
            },
        };
    }
    return { valid: true };
}
/**
 * Generate user-friendly error message for overlap conflict (Czech).
 */
export function formatOverlapError(conflict) {
    const startTime = `${conflict.hour}:00`;
    const endTime = `${conflict.hour + conflict.length}:00`;
    const clientInfo = conflict.clientNames.length > 0
        ? ` (${conflict.clientNames.join(', ')})`
        : '';
    return `Nelze uložit: Instruktor ${conflict.instructorName} již má rezervaci ` +
        `od ${startTime} do ${endTime}${clientInfo}. Vyberte prosím jiný čas.`;
}
